<template>
  <Slideover :visible="visible">
    <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
      <div class="flex items-start justify-between space-x-3">
        <h2 class="text-lg leading-7 font-medium">
          {{ title }}: {{ this.itemToBeRevised.variant_id }} &bull;
          {{ variantName }}
        </h2>
        <div class="h-7 flex items-center">
          <button
            type="button"
            aria-label="Close panel"
            @click="$emit('toggle-slideover')"
            class="text-green-300 hover:text-white transition ease-in-out duration-150"
          >
            <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
          </button>
        </div>
      </div>
    </header>

    <!-- Main -->
    <div class="flex flex-col py-3 px-2 mx-4">
      <div class="mb-3">
        <div class="mt-1 relative rounded-md shadow-sm">
          <div>
            {{ productName }}
          </div>
          <div class="mt-2">
            <label class="block text-sm font-medium leading-5 text-gray-700">
              New Variant
            </label>
            <VariantSelect
              :product-id="productId"
              :includeJumbo="isJumbo"
              @change-handler="variantChange"
            />
          </div>
        </div>
      </div>
      <div class="grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6">
        <!-- QOH -->
        <div class="sm:col-span-3">
          <label class="block text-sm font-medium leading-5 text-gray-700">
            Quantity Available
          </label>
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              :value="variantQuantityAvailable"
              disabled
              type="number"
              step="0.01"
              class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <div
              v-if="loadingVariants"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <LoadingSpinner class="text-gray-400" />
            </div>
          </div>
        </div>
        <div class="sm:col-span-3">
          <label class="block text-sm font-medium leading-5 text-gray-700">
            Estimated Variant Cost:
          </label>
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              :value="estimatedVariantCost"
              disabled
              type="number"
              step="0.01"
              class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <div
              v-if="loadingVariants"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <LoadingSpinner class="text-gray-400" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <HttpError :error="error" />

    <!-- Footer -->
    <div class="flex justify-end px-4 py-4 space-x-4">
      <span class="inline-flex rounded-md shadow-sm">
        <button
          type="button"
          @click="$emit('toggle-slideover')"
          class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
        >
          Cancel
        </button>
      </span>
      <span class="inline-flex rounded-md shadow-sm">
        <Button
          @click-handler="printLockCheck"
          :disabled="!canProcess"
          class="flex items-center space-x-2"
        >
          <span>Revise Item</span>
          <LoadingSpinner v-if="processing" class="text-white" />
        </Button>
      </span>
    </div>
  </Slideover>
</template>
<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import Button from "@/components/elements/Button.vue";
import Slideover from "@/components/layout/Slideover.vue";
import VariantSelect from "@/components/elements/VBI/VariantSelect";
import { ModalBus } from "@/eventBus";
import PrintLockedModal from "@/views/Utilities/components/modals/PrintLockedModal";

const OrderAPI = ApiFactory.get("orders");
const InventoryAPI = ApiFactory.get("inventories");
const PrintsAPI = ApiFactory.get("prints");

export default {
  name: "ReviseLineItem",
  components: {
    Button,
    Slideover,
    VariantSelect
  },
  props: {
    title: {
      type: String,
      default: "Title"
    },
    visible: {
      type: Boolean,
      default: false
    },
    itemToBeRevised: {
      type: Object,
      required: true,
      default: () => {}
    }
  },
  data() {
    return {
      error: null,
      processing: false,
      validating: false,
      validated: false,
      loadingVariants: false,
      facility_id: null,
      variantId: null,
      variants: null,
      productId: null,
      variant: null
    };
  },
  computed: {
    variantCounts() {
      return this.variant?.inventory_counts?.find(vc => {
        return Number(vc.facility_id) === Number(this.facility_id);
      });
    },
    estimatedVariantCost() {
      if (this.variant?.variant_estimated_cost?.active_cost) {
        return Number(
          this.variant?.variant_estimated_cost?.active_cost
        ).toFixed(2);
      }

      return 0.0;
    },
    variantQuantityAvailable() {
      if (this.variant?.dropship && this.variant?.selling_on_website) {
        return 1000;
      }
      return this.variantCounts
        ? Number(this.variantCounts?.total_for_sale_qoh) -
            Number(this.variantCounts?.committed_quantity)
        : 0;
    },
    canProcess() {
      if (
        this.processing ||
        this.validating ||
        this.validated ||
        this.loadingVariants
      ) {
        return false;
      }

      if (!this.variantId) {
        return false;
      }

      if (Number(this.variantId) === Number(this.itemToBeRevised.variant_id)) {
        return false;
      }

      if (this.variant?.dropship && !this.variant?.selling_on_website) {
        return false;
      }
      return this.variantQuantityAvailable > 0;
    },
    productName() {
      return this.itemToBeRevised?.variant?.product?.name;
    },
    lineItemProductId() {
      return Number(this.itemToBeRevised?.variant?.product_id);
    },
    variantName() {
      return this.itemToBeRevised?.variant?.variant_name;
    },
    isJumbo() {
      return Boolean(this.itemToBeRevised?.variant?.jumbo);
    }
  },
  watch: {
    itemToBeRevised() {
      this.productId = this.lineItemProductId;
      this.facility_id = this.itemToBeRevised.facility_id;
      this.getVariants();
    }
  },
  mounted() {
    this.productId = this.lineItemProductId;
    this.facility_id = this.itemToBeRevised.facility_id;
    this.getVariants();
  },
  methods: {
    getVariants() {
      this.loadingVariants = true;
      this.error = null;
      InventoryAPI.productVariants(this.productId, {
        with: ["inventoryCounts", "variantEstimatedCost"]
      })
        .then(async response => {
          this.variants = response.data.body;
        })
        .catch(error => {
          this.error = error;
        })
        .finally(() => {
          this.loadingVariants = false;
        });
    },
    variantChange(id) {
      this.variantId = id;
      this.variant = null;
      if (this.variantId) {
        this.variant = this.variants?.find(variant => {
          return variant.id === this.variantId;
        });
      }
    },
    async printLockCheck() {
      this.processing = true;
      const result = await PrintsAPI.checkPrintLock();
      if (result.data?.body?.status !== "unlocked") {
        ModalBus.$emit("open", {
          component: PrintLockedModal,
          closeOnOutsideClick: false
        });
        this.processing = false;
        return;
      }
      this.process();
    },
    process() {
      this.error = null;
      this.processing = true;
      OrderAPI.reviseItem(
        this.itemToBeRevised.order.SourceOrderNumber,
        this.itemToBeRevised.ItemNumber,
        {
          facility_id: this.itemToBeRevised.facility_id,
          product_id: this.productId,
          variant_id: this.variantId,
          quantity: this.itemToBeRevised.QuantityOrdered
        }
      )
        .then(this.handleSuccess)
        .catch(error => (this.error = error))
        .finally(() => (this.processing = false));
    },
    handleSuccess() {
      this.$emit("toggle-slideover");
      this.$emit("refresh-order");
    }
  }
};
</script>
